/*
  FOR FUTURE DEVS - MAPS DOCUMENTATION

  Maps package NPM page - https://www.npmjs.com/package/@react-google-maps/api
  Maps docs - https://react-google-maps-api-docs.netlify.com/
  Maps github - https://github.com/JustFly1984/react-google-maps-api/tree/master/packages/react-google-maps-api
  Google maps API docs - https://developers.google.com/maps/documentation/javascript/tutorial
*/

import {
  FC,
  HTMLProps,
  ComponentProps,
  useState,
  useReducer,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
  Ref,
} from 'react';
import getConfig from 'next/config';
import {
  GoogleMap,
  Marker,
  useLoadScript,
  MarkerClusterer,
  InfoWindow,
} from '@react-google-maps/api';
import classNames from 'classnames';

import { i18n as I18n } from 'utils/i18n';

import mapPin from '../../../public/images/map-icons/map-pin.svg';
import clusterIcon from '../../../public/images/map-icons/m1.png';

// components
import MapVenue from './mapVenue';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Venue {
  id: number;
  latLng: LatLng;
}

type CardPropNames =
  | 'showRooms'
  | 'showBookRoomsButton'
  | 'showFeatures'
  | 'floorplansButtonPosition'
  | 'imagePosition';
type CardProps = Pick<ComponentProps<typeof MapVenue>, CardPropNames>;

interface Props extends HTMLProps<HTMLDivElement>, CardProps {
  venues?: Venue[];
  loading?: boolean;
  location?: string;
  selectedVenue?: Venue;
  onSelectVenue?: () => void;
  geolocation?: LatLng;
}
const emptyList = [];

const { publicRuntimeConfig: { googleMapsApiKey } = {} } = getConfig();

const getRadiusBounds = (latLng: LatLng, radiusMiles: number = 0, google) => {
  const distance = radiusMiles * 1609.344;

  return [0, 90, 180, -90]
    .map(heading =>
      google.maps.geometry.spherical.computeOffset(
        new google.maps.LatLng(latLng),
        distance,
        heading
      )
    )
    .reduce(
      (bounds, bound) => bounds.extend(bound),
      new google.maps.LatLngBounds()
    );
};

const libraries = ['geometry'];

const SearchMap: FC<Props> = (
  {
    venues = emptyList,
    loading: venuesLoading = false,
    location,
    showRooms,
    showBookRoomsButton,
    showFeatures,
    floorplansButtonPosition,
    imagePosition,
    selectedVenue: selectedVenueProp,
    onSelectVenue,
    geolocation,
    className: classNameProp,
    ...props
  },
  forwardedRef: Ref
) => {
  const [mapInstance, setMapInstance] = useState();
  const [internalSelectedVenue, internalSelectVenue] = useState(
    selectedVenueProp
  );
  const className = classNames('search-map__container', classNameProp);

  const selectedVenue = onSelectVenue
    ? selectedVenueProp
    : internalSelectedVenue;
  const selectVenue = onSelectVenue || internalSelectVenue;

  const { isLoaded: mapLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });

  const loading = venuesLoading || !mapLoaded;

  const { google } = typeof window === 'undefined' ? {} : window;

  const mapConfig = useMemo(
    () => ({
      options: {
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        zoomControlOptions: {
          position: google ? google.maps.ControlPosition.LEFT_BOTTOM : '',
        },
      },
      mapContainerStyle: {
        height: '100%',
        width: '100%',
      },
      gestureHandling: 'greedy',
      center: {
        lat: 51.528308,
        lng: -0.3817765,
      },
    }),
    [google]
  );

  const focusOnLocation = useCallback(
    (latLng, distance = 1) => {
      mapInstance?.fitBounds(getRadiusBounds(latLng, distance, google));
      mapInstance?.setCenter(latLng);
    },
    [mapInstance, google]
  );

  const centerOnLocation = useCallback(
    latLng => {
      mapInstance?.panTo(latLng);
    },
    [mapInstance]
  );

  const latLngList = useMemo(
    () =>
      venues
        .map(({ latLng }) => latLng)
        .filter(({ lat, lng } = {}) => lat && lng),
    [venues]
  );

  const latLngJson = useMemo(() => JSON.stringify(latLngList), [latLngList]);

  const repositionMap = useCallback(
    (latLngList: LatLng[] = []) => {
      if (!mapInstance || !google || !latLngList.length) return null;

      const bounds = latLngList.reduce(
        (bounds, latLng) => bounds.extend(new google.maps.LatLng(latLng)),
        new google.maps.LatLngBounds()
      );

      return mapInstance.fitBounds(bounds);
    },
    [mapInstance, google]
  );

  const resetMap = useCallback(() => repositionMap(JSON.parse(latLngJson)), [
    repositionMap,
    latLngJson,
  ]);

  useEffect(() => {
    resetMap();
  }, [resetMap]);

  useImperativeHandle(forwardedRef, () => ({ resetMap }), [resetMap]);

  const clickedMarkerRef = useRef<boolean>(false);

  const [, rerender] = useReducer(() => ({}), {});

  useEffect(() => {
    const [latLng, ...rest] = selectedVenue
      ? [selectedVenue.latLng]
      : JSON.parse(latLngJson);

    if (rest.length === 0) {
      if (clickedMarkerRef.current) centerOnLocation(latLng);
      else focusOnLocation(latLng, 1);
      // For some reason it's necessary to force a rerender after clicking on
      // the map thumbnail in list view, to prevent the info window being
      // displayed over the top of the marker rather than just above it.
      // TODO: Find an alternative
      rerender();
    }

    clickedMarkerRef.current = false;
  }, [latLngJson, selectedVenue, focusOnLocation, centerOnLocation]);

  const noResults = latLngList.length === 0;

  useEffect(() => {
    if (!selectedVenue && geolocation && !venuesLoading && noResults) {
      focusOnLocation(geolocation, 5);
    }
  }, [selectedVenue, geolocation, venuesLoading, focusOnLocation, noResults]);

  const handleCloseInfoWindow = () => {
    selectVenue(undefined);
  };

  const renderMarkers = clusterer =>
    venues
      // not all venues seem to have a lat/lng so this stops maps blowing up
      .filter(({ latLng: { lat, lng } = {} }) => lat && lng)
      .map(venue => (
        <Marker
          key={venue.id}
          position={venue.latLng}
          icon={mapPin}
          clusterer={clusterer}
          id={venue.id}
          onClick={() => {
            clickedMarkerRef.current = true;
            selectVenue(venue);
          }}
        >
          {selectedVenue && selectedVenue.id === venue.id && (
            <InfoWindow
              id="infoWindow"
              onCloseClick={handleCloseInfoWindow}
              position={venue.latLng}
            >
              <MapVenue
                venue={selectedVenue}
                location={location}
                showRooms={showRooms}
                showBookRoomsButton={showBookRoomsButton}
                showFeatures={showFeatures}
                floorplansButtonPosition={floorplansButtonPosition}
                imagePosition={imagePosition}
                organisationName={selectedVenue.organisation.name}
              />
            </InfoWindow>
          )}
        </Marker>
      ));

  if (loadError) {
    return <div>{I18n.t('search_map.load_error')}</div>;
  }

  return (
    <div className={className} {...props}>
      {loading && <i className="fa fa-spinner spinning" />}
      {!loading && (
        <GoogleMap
          {...mapConfig}
          onLoad={setMapInstance}
          onClick={() => handleCloseInfoWindow()}
        >
          <MarkerClusterer
            averageCenter
            styles={[
              {
                backgroundPosition: 'center center',
                textColor: 'white',
                height: 30,
                width: 30,
                textSize: 16,
                fontWeight: 'bold',
                url: clusterIcon,
              },
            ]}
          >
            {clusterer => renderMarkers(clusterer)}
          </MarkerClusterer>
        </GoogleMap>
      )}
    </div>
  );
};

export default forwardRef(SearchMap);
